import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box, ButtonIcon, Flex, Loading, Page, ProgressBar, SearchSelect, Section, Typography } from 'components';

import {
  AvailableClassesApiResponse,
  LivesEvolutionCard,
  ProductDetailsApiResponse,
  ProductEvolutionDetailsApiResponse,
  ProductsEvolutionCard,
  ProductsEvolutionContentCard,
  ProductsProgress,
  useProducts,
} from 'features/products';
import { LastBadges, StudentBadgesApiResponse, useBadges } from 'features/badges';
import { createMarkup, createUrl, request } from 'app/utils';
import { useSchool } from 'features/school';
import { AxiosResponse } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useMenu } from 'features/menu';

const SquareLines = styled('i')`
  position: relative;
  position: absolute;
  left: 0;
  width: 0px;
  height: 0px;

  &::after {
    content: '';
    display: block;
    background-color: ${({ theme }) => theme.colors.primary_200};
    position: absolute;
    z-index: 10;
  }

  &::before {
    content: '';
    display: block;

    border-top: 2px solid ${({ theme }) => theme.colors.dark_150};
    border-right: 2px solid ${({ theme }) => theme.colors.dark_150};
    position: absolute;

    right: 0px;
    z-index: 1;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
    ${mediaQueries.mobile} {
      top: 1px;
      left: -25px;

      &::after {
        width: 14px;
        height: 14px;
      }

      &::before {
        top: -30px;
        width: 150px;
        height: 230px;
      }
    }

    ${mediaQueries.tablet} {
      left: -30px;

      &::after {
        width: 14px;
        height: 14px;
        top: 3px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 100px;
        height: 150px;
      }
    }

    ${mediaQueries.desktop} {
      top: 4px;
      left: -40px;

      &::after {
        width: 17px;
        height: 17px;
        top: 0px;
        left: 0px;
      }

      &::before {
        top: -40px;
        width: 150px;
        height: 280px;
      }
    }
  `;
  }}
`;

const ProductsEvolutionView = (): ReactElement => {
  const [loading, setLoading] = useState(true);

  const { apiGatewayUrl } = useSchool();
  const { productSlug, productClassUuid } = useParams();
  const { setStudentBadges, clearStudentBadges } = useBadges();
  const {
    productDetails,
    setProductDetails,
    clearProductDetails,
    productEvolutionDetails,
    setProductEvolutionDetails,
    clearProductEvolutionDetails,
    availableClasses,
    setAvailableClasses,
    clearAvailableClasses,
  } = useProducts();
  const { setBackPathname } = useMenu();
  const navigate = useNavigate();

  const fetch = async () => {
    setLoading(true);

    await Promise.all([
      request({
        url: `/api/v1/School/Products/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<ProductDetailsApiResponse>) => {
        setProductDetails(response.data);
      }),

      request({
        url: `/api/v1/School/StudentBadges`,
        method: 'post',
        baseURL: apiGatewayUrl,
        data: {
          productSlug,
          specialOnly: null,
          itensPerQuery: 8,
        },
      }).then((response: AxiosResponse<StudentBadgesApiResponse>) => {
        setStudentBadges(response.data);
      }),

      request({
        url: `/api/v1/School/ProductAssessments/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<ProductEvolutionDetailsApiResponse>) => {
        setProductEvolutionDetails(response.data);
      }),

      request({
        url: `/api/v1/School/AvailableClasses/${productSlug}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      }).then((response: AxiosResponse<AvailableClassesApiResponse>) => {
        setAvailableClasses(response.data);
      }),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (availableClasses && !availableClasses?.current?.realTimeAssessments) {
      navigate(createUrl('productClassDetails', [productSlug, productClassUuid]));
    }
  }, [availableClasses]);

  useEffect(() => {
    fetch();

    // setBackPathname(createUrl('myCourses'));
    return () => {
      clearStudentBadges();
      clearProductDetails();
      clearProductEvolutionDetails();
      clearAvailableClasses();
      // clearBackPathname();

      setBackPathname(
        productClassUuid ? createUrl('productClassDetails', [productSlug, productClassUuid]) : createUrl('productDetails', [productSlug])
      );
    };
  }, [productSlug, productClassUuid]);

  if (loading || !availableClasses?.current?.realTimeAssessments) return <Loading label="Por favor, aguarde." />;

  return (
    <>
      <Page title="Minha evolução">
        <Flex flexGrow={0} bg="dark_350">
          <Section flexWrap="nowrap" flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between">
            <Flex flexGrow={0} flexDirection="column" pt={{ m: 40, d: 40 }} pb={{ m: 60, d: 100 }}>
              <Box position="relative">
                <SquareLines />
                <Typography type="type5" color="white" fontWeight="bold" upper>
                  Minha evolução
                </Typography>
              </Box>

              <Typography type="type11" color="white" fontWeight="bold" mt={2}>
                {productDetails?.product.name}
              </Typography>
            </Flex>

            <Flex width={{ d: 450 }} ml={{ d: 60 }} flexShrink={0} flexGrow={0} />
          </Section>
        </Flex>

        <Flex py={{ m: 30, t: 60, d: 90 }} bg="gray_300" flexGrow={0}>
          <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between" flexWrap="nowrap">
            <Flex flexGrow={1} flexDirection="column">
              <Typography type="type4" fontWeight="bold" upper>
                {productSlug === 'na-rota-do-mercado-financeiro' ? 'Sua evolução na mentoria' : 'Sua evolução no curso'}
              </Typography>

              <Typography type="type1" mt={1} mb={3}>
                Informações e status de todos os módulos e conteúdos.
              </Typography>

              {!!productEvolutionDetails?.timelines?.length &&
                productEvolutionDetails?.timelines.map((timeline, timelineIndex) => (
                  <ProductsEvolutionCard
                    key={`timeline-item-${timelineIndex.toString()}-${timeline.uuid}`}
                    module={timeline}
                    classUuid={productEvolutionDetails?.productClassUuid}
                  />
                ))}

              {!!productEvolutionDetails?.items?.length && (
                <Flex flexDirection="column">
                  <Flex bg="white" mt={2} flexWrap="nowrap" justifyContent="space-between" borderTopLeftRadius={4} borderTopRightRadius={4}>
                    <Flex flexDirection="column">
                      <Typography type="type2" color="gray_600" fontWeight="bold" pt={{ m: 2, t: 3 }} px={{ m: 2, t: 3 }}>
                        <div dangerouslySetInnerHTML={createMarkup(productDetails?.product.name)} />
                      </Typography>

                      <Flex flexGrow={0} flexWrap="nowrap" alignItems="center" pt={1} pb={2} px={3}>
                        <Box flexGrow={0} flexShrink={0} width={40}>
                          <Typography type="type1" color="primary_400" fontWeight="bold">
                            {`${productEvolutionDetails?.completionPercentage}%`}
                          </Typography>
                        </Box>

                        <Flex>
                          <ProgressBar percent={productEvolutionDetails?.completionPercentage} />
                        </Flex>

                        <Flex width={140} flexShrink={0} flexGrow={0} alignItems="center" justifyContent="flex-end">
                          <>
                            {!!productEvolutionDetails?.articleCount && (
                              <>
                                <Typography type="type0" color="gray_500" pt="2px" pr="3px" pl={2}>
                                  {productEvolutionDetails?.articleCount}
                                </Typography>

                                <ButtonIcon icon="textOutline" iconWidth={13} bg="transparent" color="gray_500" />
                              </>
                            )}

                            {!!productEvolutionDetails?.videoCount && (
                              <>
                                <Typography type="type0" color="gray_500" pt="2px" pr="3px" pl={2}>
                                  {productEvolutionDetails?.videoCount}
                                </Typography>

                                <ButtonIcon icon="playBoxOutline" iconWidth={13} bg="transparent" color="gray_500" />
                              </>
                            )}

                            {!!productEvolutionDetails?.quizCount && (
                              <>
                                <Typography type="type0" color="gray_500" pt="2px" pr="3px" pl={2}>
                                  {productEvolutionDetails?.quizCount}
                                </Typography>

                                <ButtonIcon icon="checkboxOutline" iconWidth={13} bg="transparent" color="gray_500" />
                              </>
                            )}
                          </>
                        </Flex>
                      </Flex>
                    </Flex>

                    <Flex flexGrow={0} pr={3}></Flex>
                  </Flex>

                  <Flex flexDirection="column" bg="gray_100" borderBottomLeftRadius={4} borderBottomRightRadius={4}>
                    {productEvolutionDetails?.items.map((content, contentIndex) => (
                      <ProductsEvolutionContentCard
                        key={`items-item-${contentIndex.toString()}-${content.uuid}`}
                        content={content}
                        index={contentIndex}
                        productSlug={productSlug}
                        classUuid={productEvolutionDetails.productClassUuid}
                        contentCount={productEvolutionDetails.contentCount}
                      />
                    ))}
                  </Flex>
                </Flex>
              )}

              {!!productEvolutionDetails?.lives?.length && (
                <LivesEvolutionCard
                  lives={productEvolutionDetails?.lives}
                  liveCompletionPercentage={productEvolutionDetails?.liveCompletionPercentage || 0}
                />
              )}
            </Flex>

            <Flex width={{ d: 450 }} ml={{ d: 60 }} flexShrink={0} flexGrow={0} position="relative">
              <Flex position={{ d: 'absolute' }} mt={{ t: 30, d: -295 }} width={1} flexDirection="column">
                {!!availableClasses?.availables?.length && availableClasses?.availables?.length > 1 && (
                  <Box mb="10px">
                    <SearchSelect
                      name="itensPerPage"
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                        if (event.target.value) {
                          navigate(`${createUrl('productClassEvolution', [productSlug, event.target.value])}`);
                        }
                      }}
                    >
                      {availableClasses.availables.map((availableClass) => (
                        <option
                          selected={availableClass.productClassUuid === (productClassUuid || availableClasses?.current.productClassUuid)}
                          key={`availableClass-${availableClass.productClassUuid}`}
                          value={availableClass.productClassUuid}
                        >
                          {availableClass.description}
                        </option>
                      ))}
                    </SearchSelect>
                  </Box>
                )}

                <Flex py={{ m: 20, t: 30, d: 40 }} flexDirection="column" bg="white" textAlign="center" borderRadius={4}>
                  <Typography as="span" type="type5" lineHeight="1.3" color="dark_350" fontWeight="bold">
                    {productSlug === 'na-rota-do-mercado-financeiro' ? 'Sua evolução na mentoria' : 'Sua evolução no curso'}
                  </Typography>

                  <Box mt={3}>
                    <ProductsProgress percent={productEvolutionDetails?.completionPercentage} />
                  </Box>
                </Flex>

                <Flex mt={3}>
                  <LastBadges />
                </Flex>
              </Flex>
            </Flex>
          </Section>
        </Flex>
      </Page>

      {/* <Page title="Minha evolução">
      <Flex flexGrow={0} bg="dark_350">
        <Section flexDirection={{ m: 'column', d: 'row' }} justifyContent="space-between">
          <Flex
            flexShrink={0}
            maxWidth={{ d: 630 }}
            flexGrow={0}
            flexDirection="column"
            pt={{ m: 40, d: 40 }}
            pb={{ m: 60, d: 100 }}
          >
            <Box position="relative">
              <SquareLines />
              <Typography type="type5" color="white" fontWeight="bold" upper>
                Minha evolução
              </Typography>
            </Box>

            <Typography type="type11" color="white" fontWeight="bold" mt={2}>
              {productDetails.product.name}
            </Typography>
          </Flex>
        </Section>
      </Flex>

      <Flex flexGrow={0} bg="gray_300">
        <Section py={{ m: 20, t: 40, d: 60 }}>
          <Flex flexWrap={{ m: 'wrap', d: 'nowrap' }}>
            <Flex mr={{ m: 0, d: 60 }}>
              <Flex flexDirection="column">
                <Typography type="type4" fontWeight="bold" upper>
                  Sua evolução no curso
                </Typography>

                <Typography type="type1" mt={1} mb={3}>
                  Informações e status de todos os módulos e aulas.
                </Typography>

                {productEvolutionDetails.timelines.map((timeline) => (
                  <ProductsEvolutionCard key={`timeline-item-${timeline.uuid}`} module={timeline} />
                ))}
              </Flex>
            </Flex>

            <Flex mt={{ m: 30, t: 50, d: -200 }} zIndex={100} flexDirection="column">
              <Flex width={{ m: '100%', t: 450 }} flexShrink={0} flexGrow={0} mx="auto">



              </Flex>
            </Flex>
          </Flex>
        </Section>
      </Flex>
    </Page> */}
    </>
  );
};
export default ProductsEvolutionView;
